
import { Component, Vue } from 'vue-property-decorator';
import { Route } from 'vue-router';
import DateRange from '@/components/DateRange.vue';
import UrlWithQrCode from '@/components/UrlWithQrCode.vue';
import api from '@/api';
import Util from '@/utils/util';
import Swal from 'sweetalert2';

const { couponActivity } = api;

interface CouponActivity {
  activityId: string;
  sequence: number;
  activityTypeName: string;
  title: string;
  hasBanner: boolean;
  quantity: number;
  isEnabled: boolean;
}

@Component({
  components: { DateRange, UrlWithQrCode },
})
export default class CouponActivityList extends Vue {
  private data: {
    data: CouponActivity[];
    totalCount: number;
    pageSize: number;
    pageNo: number;
  } = { data: [], totalCount: 0, pageSize: 20, pageNo: 0 };

  private loading = false;

  private panel = 0;

  private headers = [
    { text: 'ID', value: 'sequence' },
    { text: '類型', value: 'activityTypeName' },
    { text: '頁面標題', value: 'title' },
    { text: 'Banner', value: 'hasBanner' },
    { text: '商品品項', value: 'quantity' },
    { text: '頁面連結', value: 'qrcode' },
    { text: '啟用', value: 'isEnabled' },
    { text: '編輯', value: 'edit' },
  ];

  private activityTypes: Array<{ text: string; value: number }> = [
    { text: '活動', value: 1 },
    { text: '店家', value: 2 },
  ];

  private selected: CouponActivity | null = null;

  private get hyperlink() {
    return this.selected
      ? `${process.env.VUE_APP_DISCOUNT_COUPON_ACTIVITY_WEB}?id=${this.selected.activityId}`
      : null;
  }

  private get totalCount() {
    return this.data.totalCount;
  }

  private get pageCount() {
    return Math.ceil((this.data.totalCount || 0) / (this.data.pageSize || 20));
  }

  private get collection() {
    return [...(this.data.data || [])];
  }

  private filter = {
    activityType: this.$route.query.activityType || undefined,
    q: this.$route.query.q || undefined,
    pageSize: Util.isNullOrUndefined(this.$route.query.pageSize)
      ? 20
      : +this.$route.query.pageSize,
    pageNo: Util.isNullOrUndefined(this.$route.query.pageNo)
      ? 1
      : +this.$route.query.pageNo,
  };

  private async updatePage(page: number) {
    this.filter.pageNo = page;
    await this.query();
  }

  private async search() {
    this.filter.pageNo = 1;
    await this.query();
  }

  private async query() {
    const query = {
      ...this.filter,
      pageSize: `${this.filter.pageSize}`,
      pageNo: `${this.filter.pageNo}`,
    };
    if (this.isQueryChanged(query)) {
      await this.$router.replace({ query });
    }
    await this.requestData();
  }

  private async reset() {
    this.filter = {
      activityType: undefined,
      q: undefined,
      pageSize: 20,
      pageNo: 1,
    };
    await this.query();
  }

  private async changeEnableStatus(activityId: string, isEnabled: boolean) {
    const word = isEnabled ? '停用' : '啟用';
    await Swal.fire({
      title: `您確定要${word}嗎?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    }).then(async ({ isConfirmed }) => {
      if (isConfirmed) {
        const { status } = isEnabled
          ? await couponActivity.disable(activityId)
          : await couponActivity.enable(activityId);
        if (status) {
          this.requestData();
        }
      }
    });
  }

  private isQueryChanged(query: {
    [key: string]: string | Array<string | null> | null | undefined;
  }): boolean {
    return !Object.keys(query).every(
      (key: string) => query[key]?.toString() === this.$route.query[key],
    );
  }

  private async requestData() {
    this.loading = true;
    const { data } = await couponActivity.list(this.filter);
    this.data = data;
    this.loading = false;
  }

  private async beforeRouteEnter(
    to: Route,
    from: Route,
    next: (to?: ((vm: CouponActivityList) => void) | boolean) => void,
  ) {
    await next(async (vm) => {
      await vm.requestData();
    });
  }
}
